import { graphql, useStaticQuery } from "gatsby"
import React, { useState } from "react"
import SharedTextButton from '../sharedTextButton/sharedTextButton'
import * as styles from './status.module.scss'
import moment from "moment"
import { orderBy } from "lodash"
import ReactMarkdown from "react-markdown"

export default ({ block }) => {
  const {
    allServiceComponentStatusesJson: { nodes: componentStatuses }
    } = useStaticQuery(graphql`
    {
      allServiceComponentStatusesJson {
        nodes {
          title
          color
        }
      }
    }`)

  const maintenanceItems = block.maintenanceItems ?? []

  const outages = block.outages ?? []

  const [selectedMaintenanceIndex, setSelectedMaintenanceIndex] = useState<number | null>(null)

  const isUnderMaintenance = (component) => {
    const now = moment().valueOf()
    return !!maintenanceItems.find(m =>
      m.components && m.components.some(c => c.serviceComponent === component.serviceComponent) &&
      now >= moment(m.startTime).valueOf() &&
      now <= moment(m.endTime).valueOf()
    )
  }

  const getOutage = (component) => {
    return outages.find(o =>
      o.components.some(c => c.serviceComponent === component.serviceComponent))
  }

  const getOutageColor = (o) => {
    if (o.serviceIncidentStatus === 'Resolved') {
      return componentStatuses.find(s => s.title === 'Operational')?.color ?? '#074786'
    }

    return componentStatuses.find(s => s.title === o.serviceComponentStatus)?.color ?? '#074786'
  }

  const getComponentOutageColor = (o) => {
    return o.serviceIncidentStatus === 'Resolved'
      ? componentStatuses.find(s => s.title === 'Operational')?.color ?? '#074786'
      : getOutageColor(o)
  }

  const getStatus = (c) => {
    const outage = getOutage(c)

    return outage && outage.serviceIncidentStatus !== 'Resolved'
      ? outage.serviceComponentStatus
      : 'Operational'
  }

  const maintenanceColor = componentStatuses.find(s => s.title ===   'Maintenance')?.color ?? '#074786'

  const getColor = (c) => {
    const outage = getOutage(c)

    return isUnderMaintenance(c)
      ? maintenanceColor
      : outage
      ? getComponentOutageColor(outage) ?? '#074786'
      : componentStatuses.find(s => s.title === 'Operational')?.color ?? '#074786'
  }

  const getUpdates = (updates) => {
    const updatesWithIndexes =  orderBy(updates, u => moment(u.time).valueOf())
      .map((u,i) => ({...u, index: i + 1 }))

    return orderBy(updatesWithIndexes, u => u.index, 'desc')
  }

  const maintenance = orderBy(maintenanceItems, m => moment(m.startTime).valueOf())

  const selectedMaintenance = selectedMaintenanceIndex != null
    ? maintenance[selectedMaintenanceIndex]
    : null

  return <section className={styles.section}>
    <div className={styles.container}>
      <h1>{block.blockTitle}</h1>
      <p className={styles.description}>{block.description}</p>
      <div className={styles.formButton}>
        <SharedTextButton button={block.sharedTextButton} />
      </div>

      <div className={styles.components}>
        {block.components.map((c,i) => (
          <div key={i}>
            <span>{c.serviceComponent}</span>
            <span
              style={{ color: getColor(c)}}
            >{
              isUnderMaintenance(c) ? 'Under Maintenance' : getStatus(c)
            }</span>
          </div>
        ))}
      </div>

      {outages && outages.length > 0 && <div className={styles.outages}>
        {outages.map((o,i) => (
          <div key={i} className={styles.outage} style={{ borderColor: getOutageColor(o)}}>
            <h3
              style={{ backgroundColor: getOutageColor(o)}}
            >{o.outageTitle} <span className={styles.divider}>|</span><br className={styles.linebreak}></br>&nbsp;Status: <strong>{o.serviceIncidentStatus}</strong></h3>
            {getUpdates(o.updates).map((u,ui) => (
              <div key={ui} className={styles.outageUpdate}>
                <ReactMarkdown>{`**Update ${u.index}** - ` + u.message}</ReactMarkdown>
                <span className={styles.time}>{moment(u.time).format('MMM DD, YYYY HH:mm')} CDT</span>
                {ui != o.updates.length - 1 && 
                  <hr></hr>
                }
              </div>
            ))}
          </div>
        ))}
      </div>}

      {maintenance && maintenance.length > 0 && <div className={styles.maintenance}>
        <h3
          className={styles.maintenanceTitle}
          style={{ backgroundColor: maintenanceColor }}
        >{block.maintenanceTitle ?? 'Maintenance'}</h3>

        {selectedMaintenance &&
        <div className={styles.selectedMaintenance}
          style={{ borderColor: maintenanceColor }}
        >
          <h3>{selectedMaintenance.title}</h3>

          <h5>{selectedMaintenance.subtitle}</h5>
          
          <div className={styles.content}>
            <div className={styles.contentItem}>
              <span>Start Time:</span>
              <span>
                {moment(selectedMaintenance.startTime).format('MMM DD, YYYY')}
                &nbsp;at&nbsp;
                {moment(selectedMaintenance.startTime).format('hh:mm A')} (Central Time)
              </span>
            </div>
            <div className={styles.contentItem}>
              <span>End Time:</span>
              <span>
                {moment(selectedMaintenance.endTime).format('MMM DD, YYYY')}
                &nbsp;at&nbsp;
                {moment(selectedMaintenance.endTime).format('hh:mm A')} (Central Time)
              </span>
            </div>
            <div className={styles.contentItem}>
              <span>Services Impacted:</span>
              <span>
                {selectedMaintenance.components.map(({serviceComponent}) => serviceComponent).join(' & ')}
              </span>
            </div>
          </div>
          <ReactMarkdown className={styles.summary}>{selectedMaintenance.summary}</ReactMarkdown>

          <div className={styles.content}>
            <p className={styles.supportMessage}>{selectedMaintenance.supportMessage}</p>
            <p className={styles.footer}>{selectedMaintenance.footer}</p>
          </div>
          
        </div>}

        <div className={styles.maintenanceItems}>
          {maintenance
            .map((m,i) => (
              <div key={i} className={styles.maintenanceItem + `${i === selectedMaintenanceIndex ? ` ${styles.active}` : ''}`}
                style={{
                  borderColor: maintenanceColor,
                }}
              >
                <p className={styles.date}>{moment(m.startTime).format('MMM DD, YYYY')}</p>
                <button
                  onClick={() => setSelectedMaintenanceIndex(i)}
                  className={styles.moreInfo}
                  style={{ backgroundColor: maintenanceColor }}
                >More Info</button>
              </div>
            ))}
        </div>
      </div>}
    </div>
    
  </section>
}
